import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { fetchPartners } from '../../actions/partners';
import PartnersList from '../../components/PartnersList';
import Module from '../../components/Module';
import ModuleContentList from '../../components/ModuleContentList';
import PageTitle from '../../components/PageTitle';
import Pagination from '../../components/Pagination';
import Main from '../../components/Main';
import styles from './PartnerListing.style';
import SearchBar from '../../components/SearchBar';
import { hasRole } from '../../utils/auth';
import theme from '../../theme.style';

const PartnersListing = () => {
  const dispatch = useDispatch();
  const [resetPagination, setResetPagination] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = React.useState(0);
  const [filterState, setFilterState] = useState({});
  const [lastIndex, setLastIndex] = useState(0);

  const partners = useSelector(state =>
    get(state, 'pagination.partners.results', []).map(tokenId =>
      get(state, `entities.partners.${tokenId}`),
    ),
  );

  const handlePageChange = paginationData => {
    setResetPagination(false);
    dispatch(
      fetchPartners({
        start: paginationData.currentPageIndex * paginationData.pageLimit,
        count: paginationData.pageLimit,
        ...filterState,
      }),
    );
  };

  const filterPartners = async filterCriteria => {
    setLoading(true);
    const response = await dispatch(fetchPartners(filterCriteria));
    setTotalCount(get(response, `meta.totalCount`, 0));
    setLoading(false);
  };

  const updatePartnerFilters = partnerFilters => {
    setFilterState(partnerFilters);
    filterPartners(partnerFilters);
  };

  const updateSearchFilter = async query => {
    const newFilterState = {
      ...filterState,
      query,
    };
    updatePartnerFilters(newFilterState);
  };

  const handleClear = () => {
    setResetPagination(true);
    updateSearchFilter('');
  };

  const handleSearch = async query => {
    setLastIndex(0);
    setResetPagination(true);
    updateSearchFilter(query);
  };

  const onSiloChange = async (event, option) => {
    const newFilterState = {
      ...filterState,
      filterSilo: option.silo,
    };

    setResetPagination(true);
    updatePartnerFilters(newFilterState);
  };

  const siloOptions = [
    { key: 0, text: 'Any', silo: '' },
    { key: 1, text: 'Azure', silo: '1p-azure' },
    { key: 2, text: 'TLA', silo: '3p-tla' },
  ];

  useEffect(() => {
    async function loadPartners() {
      const response = await dispatch(fetchPartners());
      setTotalCount(get(response, `meta.totalCount`, 0));
      setLoading(false);
    }
    loadPartners();
  }, [dispatch]);

  return (
    <>
      <PageTitle>Partners</PageTitle>

      <Main>
        <div className={styles.searchSection}>
          <SearchBar
            placeholder="Search partners"
            ariaLabel="Search"
            underlined
            onChange={handleSearch} // when you type
            onClear={handleClear} // when you click X
            data-testid="search-input"
            className={styles.search}
          />
          {hasRole('msft') && (
            <Dropdown
              options={siloOptions}
              label="Silo"
              className={styles.filter}
              placeholder="All Silos"
              styles={{
                title: { backgroundColor: theme.palette.neutralWhite },
                dropdown: { fontWeight: 'normal' },
                dropdownItem: { fontWeight: 'normal' },
              }}
              onChange={onSiloChange}
            />
          )}
        </div>

        {loading && partners.length === 0 && (
          <Module>
            <ModuleContentList>
              <PartnersList
                items={[]}
                shimmered
                enableShimmer
                noBorderBottom
                data-testid="partner-list-items-shimmer"
              />
            </ModuleContentList>
          </Module>
        )}

        {!loading && partners.length === 0 && (
          <div className={styles.resultsMessage}>
            <Text as="h4" block>
              {partners.length + lastIndex} {partners.length === 1 ? 'Result' : 'Results'}
            </Text>
            {partners.length === 0 && (
              <Text as="p" block className={styles.noResultsSentence}>
                No partners found
              </Text>
            )}
          </div>
        )}
        {/* If someone searched but got 0 results, hide the list */}
        {partners.length !== 0 && (
          <Module>
            <ModuleContentList>
              <PartnersList shimmered enableShimmer={loading} items={partners} noBorderBottom />
            </ModuleContentList>
            <Pagination
              totalRecords={totalCount}
              handlePageChange={handlePageChange}
              resetPagination={resetPagination}
            />
          </Module>
        )}
      </Main>
    </>
  );
};

export default PartnersListing;
